import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import { syncVirtualResourcePathMetadata } from '@/features/virtual-resource/rules'
import DataFileMap from '@/model/Resource/DataFileMap'

export const VIRTUAL_RESOURCES_FILE = 'virtual_resources.json'

const RESOURCE_CATEGORY_KEYS: ResourceCategoryKey[] = [
  'audio',
  'graphic',
  'font',
  'video',
]

function normalizeVirtualResourceRoot(category: ResourceCategoryKey, root: DataFileMap | null): DataFileMap | null {
  return root ? syncVirtualResourcePathMetadata(category, root) : null
}

export type VirtualResourcesFileV1 = {
  version: 1
  roots: Partial<Record<ResourceCategoryKey, DataFileMap | null>>
}

/** 生成所有资源分类都为空的默认结构。 */
function emptyRoots(): Record<ResourceCategoryKey, DataFileMap | null> {
  return {
    audio: null,
    graphic: null,
    font: null,
    video: null,
  }
}

/** 将 `virtual_resources.json` 文本解析为运行时资源树结构。 */
export function parseVirtualResourcesJson(text: string): Record<ResourceCategoryKey, DataFileMap | null> {
  const out = emptyRoots()
  let raw: unknown
  try {
    raw = JSON.parse(text) as unknown
  } catch {
    return out
  }
  if (!raw || typeof raw !== 'object') return out
  const v = (raw as { version?: number; roots?: unknown }).version
  const roots = (raw as { roots?: unknown }).roots
  if (v !== 1 || !roots || typeof roots !== 'object') return out
  const keys: ResourceCategoryKey[] = ['audio', 'graphic', 'font', 'video']
  for (const k of keys) {
    const chunk = (roots as Record<string, unknown>)[k]
    if (chunk == null) {
      out[k] = null
      continue
    }
    try {
      out[k] = normalizeVirtualResourceRoot(k, new DataFileMap(chunk as object))
    } catch {
      out[k] = null
    }
  }
  return out
}

/** 将运行时资源树序列化为磁盘/云端统一 JSON 格式。 */
export function serializeVirtualResources(
  roots: Partial<Record<ResourceCategoryKey, DataFileMap | null>>,
): string {
  const payload: VirtualResourcesFileV1 = { version: 1, roots: {} }
  const keys: ResourceCategoryKey[] = ['audio', 'graphic', 'font', 'video']
  for (const k of keys) {
    const node = roots[k]
    payload.roots[k] = normalizeVirtualResourceRoot(k, node ?? null)
  }
  return JSON.stringify(
    {
      version: payload.version,
      roots: Object.fromEntries(
        keys.map((k) => {
          const n = payload.roots[k]
          return [k, n ? n.toJson() : null]
        }),
      ),
    },
    null,
    0,
  )
}

/** 从工程 `Data` 目录读取虚拟资源树；不存在时返回空结构。 */
export async function readVirtualResourcesFromDataDir(
  dataDir: FileSystemDirectoryHandle,
): Promise<Record<ResourceCategoryKey, DataFileMap | null>> {
  try {
    const fh = await dataDir.getFileHandle(VIRTUAL_RESOURCES_FILE)
    const file = await fh.getFile()
    const text = await file.text()
    return parseVirtualResourcesJson(text)
  } catch {
    return emptyRoots()
  }
}

/** 将虚拟资源树写回工程 `Data/virtual_resources.json`。 */
export async function writeVirtualResourcesToDataDir(
  dataDir: FileSystemDirectoryHandle,
  roots: Partial<Record<ResourceCategoryKey, DataFileMap | null>>,
): Promise<void> {
  const hasAny = RESOURCE_CATEGORY_KEYS.some((k) => roots[k] != null)
  if (!hasAny) {
    try {
      await dataDir.removeEntry(VIRTUAL_RESOURCES_FILE)
    } catch {
      /* 不存在则忽略 */
    }
    return
  }
  const fh = await dataDir.getFileHandle(VIRTUAL_RESOURCES_FILE, { create: true })
  const w = await fh.createWritable()
  await w.write(new TextEncoder().encode(serializeVirtualResources(roots)))
  await w.close()
}
